import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import Underline from '../../components/underline';

import { Container } from './container';
import { Heading } from './heading';
import { Label } from './label';
import { Text } from './text';

import { fontSizes, media } from '../styles';

const propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  bodyText: PropTypes.string,
  bodyTextSize: PropTypes.string,
  label: PropTypes.string,
  headingLevel: PropTypes.number,
  isLandingPageWidth: PropTypes.bool,
  isPageLevelTitleSet: PropTypes.bool,
  condensed: PropTypes.bool,
  underlinedText: PropTypes.string,
  underlineWidth: PropTypes.number,
  underlineType: PropTypes.string,
  underlineBottom: PropTypes.number,
  alignLeft: PropTypes.bool,
  children: PropTypes.any
};

/**
 * Renders a label, title, and bodyText (<Text> component) with the correct styling.
 *
 * Usage notes: For top of page titles use isPageLevelTitleSet to adjust heading level and give TitleSet top section
 * padding. For L1 pages (ie /services, /industries) use isLandingPageWidth to widen maxWidth. In certain cases,
 * the above flags may not be compatible with spacing on page design. For this reason there is also a headingLevel flag.
 * See src/templates/partnership-detail.jsx for an example.
 *
 * For proper alignment of centered titles, this component will require a parent element using appropriate flex styles.
 * In most cases, it is recommended to place TitleSet directly inside a <Container sectionContainer>.
 *
 * @component
 * @param {*} props
 * @prop {string} id
 * @prop {string} title
 * @prop {string} bodyText
 * @prop {string} bodyTextSize
 * @prop {string} label
 * @prop {number} headingLevel
 * @prop {boolean} isLandingPageWidth
 * @prop {boolean} isPageLevelTitleSet
 * @prop {string} underlinedText
 * @prop {number} underlineWidth
 * @prop {string} underlineType
 * @prop {number} underlineBottom
 * @prop {boolean} alignLeft
 */
const TitleSet = props => {
  const {
    id,
    title,
    bodyText,
    bodyTextSize = 'large',
    label,
    headingLevel,
    isLandingPageWidth,
    noMaxWidth,
    isPageLevelTitleSet,
    underlinedText,
    underlineWidth,
    underlineType,
    underlineBottom,
    alignLeft,
    extendedBodyWidth
  } = props;

  const renderTitleSetContents = () => {

    const calculateUnderlineBottom = () => {
      const extendedBaselineCharacters = ['q', 'y', 'p', 'g', 'j']

      if (extendedBaselineCharacters.some(char => underlinedText.includes(char))) {
        return 8;
      } else {
        return underlineBottom;
      }
    }

    const calculateUnderlineWidth = () => {
      return underlineWidth ? underlineWidth : title.length * 7.5;
    }

    return (
      <TitleSetWrapper
        id={id}
        isLandingPageWidth={isLandingPageWidth}
        noMaxWidth={noMaxWidth}
        isPageLevelTitleSet={isPageLevelTitleSet}
        alignLeft={alignLeft}
      >
        <LabelAndHeading>
          {label && <TitleSetLabel size={'large'}>{label}</TitleSetLabel>}
          {title && (
            <TitleSetHeading
              isLandingPageWidth={isLandingPageWidth}
              noMaxWidth={noMaxWidth}
              level={headingLevel ? headingLevel : isPageLevelTitleSet ? 1 : 2}
            >
              {underlinedText ? (
                <Underline
                  phrase={title}
                  underlinedText={underlinedText}
                  underlineType={underlineType}
                  underlineWidth={calculateUnderlineWidth()}
                  bottom={calculateUnderlineBottom()}
                />
              ) : (
                title
              )}
            </TitleSetHeading>
          )}
        </LabelAndHeading>
        {bodyText && <TitleSetText bodyTextSize={bodyTextSize} extendedBodyWidth={extendedBodyWidth}>{bodyText}</TitleSetText>}
      </TitleSetWrapper>
    );
  };

  if (isPageLevelTitleSet) {
    return (
      <PageLevelTitleContainer id={'title-set-container'}>
        {renderTitleSetContents()}
      </PageLevelTitleContainer>
    )
  } else {
    return renderTitleSetContents();
  }
};

const PageLevelTitleContainer = styled(props => <Container {...props} />)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 120px 80px;
  
  @media screen and (max-width: ${media.mediumTop}) {
    padding: 60px 20px;
  }
`;

const TitleSetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ alignLeft }) => (alignLeft ? 'left' : 'center')};
  text-align: ${({ alignLeft }) => (alignLeft ? 'left' : 'center')};
  gap: 12px;
  max-width: ${({ isLandingPageWidth, noMaxWidth }) => noMaxWidth ? 'none' : isLandingPageWidth ? '1000px' : '800px' };
`;

const LabelAndHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleSetLabel = styled(props => <Label {...props} />)`
  margin: 0;
`;

const TitleSetHeading = styled(props => <Heading {...props} />)`
  max-width: ${({ isLandingPageWidth, noMaxWidth}) => noMaxWidth ? 'none' : isLandingPageWidth ? '1000px' : '800px'};
`;

const TitleSetText = styled(props => <Text {...props} />)`
  max-width: ${({ extendedBodyWidth }) => (extendedBodyWidth ? '800px' : '680px')};
  vertical-align: bottom;
  white-space: pre-line;
  font-size: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].fontSize};
  line-height: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].lineHeight};
    
  @media screen and (max-width: ${media.mediumBottom}) {
    font-size: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].mobile.fontSize};
    line-height: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].mobile.lineHeight};
  }
  
  // This p tag needs to be here to account for bodyText where renderRichText() is used.
  p {
    font-size: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].fontSize};
    line-height: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].lineHeight};
    max-width: 680px;
    
    @media screen and (max-width: ${media.mediumBottom}) {
      font-size: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].mobile.fontSize};
      line-height: ${({ bodyTextSize }) => fontSizes.paragraph[bodyTextSize].mobile.lineHeight};
    }
  }
`;

TitleSet.propTypes = propTypes;

export { TitleSet };
